import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { InstagramComparePosts } from '@anytag/graphql';
import { HelpIcon, MediaPreviewWithMetrics } from '@anytag/shared-atoms';
import { THEME } from '@anytag/shared-constants';
import { ControlPostDetailsModalType, usePostDetailsParam, useSelectItem } from '@anytag/shared-hooks';
import { InstagramComparePostDetailsModal } from '@anytag/shared-molecules';
import { bigIntFormatter } from '@anytag/shared-utils';
import {
  AnalyticsComparePostsHabit,
  AnalyticsComparePostsTable,
  AnalyticsComparePostsSlider,
  AnalyticsComparePostsAccount,
  AnalyticsComparePostsHashtags,
  AnalyticsComparePostsHabitModal,
  AnalyticsComparePostsHabitModalProps
} from '../../shared';
import { AnalyticsInstagramComparableAccountType } from '../types';
import {
  AnalyticsCompareInstagramHashtagsModal,
  AnalyticsCompareInstagramHashtagsModalProps
} from './AnalyticsCompareInstagramHashtagsModal';

interface TemplateProps {
  data: InstagramComparePosts;
  comparableAccounts: AnalyticsInstagramComparableAccountType[];
}

export const Template = ({ data, comparableAccounts }: TemplateProps) => {
  const { t } = useTranslation();
  const { setPostDetailsParam } = usePostDetailsParam();
  const [postsModal, setPostsModal] = useState<ControlPostDetailsModalType[]>([]);
  const {
    setSelectedItem: setPostingHabit,
    selectedItem: selectedPostingHabit,
    resetSelectedItem: resetPostingHabit
  } = useSelectItem<Pick<AnalyticsComparePostsHabitModalProps, 'account' | 'data'>>();
  const {
    setSelectedItem: setHashtags,
    selectedItem: selectedHashtags,
    resetSelectedItem: resetHashtags
  } = useSelectItem<Pick<AnalyticsCompareInstagramHashtagsModalProps, 'account' | 'hashtags'>>();

  const composeData = [
    data.mainAccount,
    ...(data.compareAccounts.filter((account) => account.id !== data.mainAccount.id) || [])
  ];
  const tableData = comparableAccounts.map((account) => ({
    account,
    ...composeData.find((item) => item.id === account.id)
  }));
  const columns: ColumnDef<(typeof tableData)[number]>[] = [
    {
      size: 160,
      accessorKey: 'account',
      header: t<string>('HeaderColumn.Account'),
      cell: ({ row }) => (
        <AnalyticsComparePostsAccount
          account={{
            name: row.original.account.username,
            avatar: row.original.account.avatarUrl
          }}
        />
      )
    },
    {
      size: 2000, // take all the space
      accessorKey: 'postingHabit',
      header: () => (
        <div className="flex items-center">
          <div className="flex flex-[1.2] items-center pr-4 pl-2">
            <HelpIcon className="mr-1" help={t('Tooltip.comparePostingHabits')} />
            {t('HeaderColumn.Posting Habits')}
          </div>
          <div className="flex flex-1 items-center pl-4">
            <HelpIcon className="mr-1" help={t('Tooltip.comparePopularHashtags')} />
            {t('HeaderColumn.Popular Hashtags')}
          </div>
        </div>
      ),
      cell: ({ row }) => (
        <>
          <div className="box-border flex min-h-22.5 py-3">
            <AnalyticsComparePostsHabit
              postsHabit={row.original.postsHabit || []}
              css={{ flex: 1.2, paddingTop: '4px', paddingBottom: '4px', borderRight: THEME.border.base }}
              buttonProps={{
                onClick: () =>
                  row.original.account && row.original.postsHabit
                    ? setPostingHabit({
                        data: row.original.postsHabit,
                        account: { name: row.original.account.username, avatar: row.original.account.avatarUrl }
                      })
                    : undefined
              }}
            />

            <AnalyticsComparePostsHashtags
              css={{ flex: 1, paddingTop: '4px', paddingBottom: '4px' }}
              hashtags={
                row.original.popularHashtags?.reduce<string[]>(
                  (acc, curr) => (curr?.hashtag ? [...acc, curr.hashtag] : acc),
                  []
                ) || []
              }
              buttonProps={{
                onClick: () =>
                  row.original.account && row.original.popularHashtags?.length
                    ? setHashtags({
                        hashtags: row.original.popularHashtags,
                        account: { name: row.original.account.username, avatar: row.original.account.avatarUrl }
                      })
                    : undefined
              }}
            />
          </div>

          <AnalyticsComparePostsSlider
            posts={row.original.feedPosts || []}
            renderPost={(post) => (
              <MediaPreviewWithMetrics
                objectFit="contain"
                src={post.thumbNail}
                css={THEME.mediaPreview.default}
                onClick={() => {
                  if (postsModal.length && postsModal.find((item) => item.id === post.id)) {
                    setPostDetailsParam?.(post.id);
                  } else if (row.original.feedPosts?.length) {
                    setPostsModal([...row.original.feedPosts]);
                    setPostDetailsParam?.(post.id);
                  }
                }}
                statistics={[
                  { label: t('Like'), value: bigIntFormatter(post.likes, 0, { k: true, m: true }) },
                  { label: t('General.Comments'), value: bigIntFormatter(post.comments, 0, { k: true, m: true }) }
                ]}
              />
            )}
          />
        </>
      )
    }
  ];

  return (
    <>
      <AnalyticsComparePostsTable data={tableData} columns={columns} />

      <AnalyticsComparePostsHabitModal
        onClose={resetPostingHabit}
        open={!!selectedPostingHabit}
        {...selectedPostingHabit}
      />

      <AnalyticsCompareInstagramHashtagsModal open={!!selectedHashtags} onClose={resetHashtags} {...selectedHashtags} />

      <InstagramComparePostDetailsModal posts={postsModal} />
    </>
  );
};
